import React, { useContext, useEffect, useState } from 'react';
import { Typography, Space, Button, Table, Tag, Checkbox, Drawer, Modal, Input, Select, Spin, Alert, Empty, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { CoverSwapLists, AbsentAvailability } from './ShiftBoardPage';
import { AvailabilityDrawer } from './AvailabilityPage';

// Shift Swap Forum > Explore Possible Swap (routes/shift_swap.py): someone's
// shifts today to 21 days on; a shift opens who could swap it (the board's
// lists, as if they were out), and it can be published to the forum. A
// manager (WFM Skills 3) picks anyone on their Visible Teams, sees Could
// cover, and may untick the filters; for anyone else Could cover is hidden
// and Hide people on leave / Only matching availability stay on.
const fmtShift = (x) => `${dayjs(x.Start).format('ddd MM/DD')} ${dayjs(x.Start).format('h:mm A')} – ${dayjs(x.End).format('h:mm A')}`;

export default function ShiftSwapExplorePage() {
  const currentUser = useContext(UserContext);
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);       // null: oneself
  const [people, setPeople] = useState([]);
  const [data, setData] = useState(null);
  const [shift, setShift] = useState(null);
  const [publishing, setPublishing] = useState(null);

  useEffect(() => {
    api.get('/api/shift-swap/people').then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  }, []);
  const load = () => {
    setData(null);
    api.get('/api/shift-swap/shifts', { params: userId ? { user_id: userId } : {} }).then((r) => setData(r.data))
      .catch((e) => { setData({ shifts: [], error: e.response?.data?.error || 'Could not load the shifts.' }); });
  };
  useEffect(load, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const self = !userId || userId === currentUser?.ID;
  return (
    <>
      <Space align="center" wrap style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Explore Possible Swap</Typography.Title>
        {people.length > 0 && (
          <Select style={{ width: 280 }} showSearch optionFilterProp="label" placeholder="Me"
            value={userId} onChange={(v) => setUserId(v ?? null)} allowClear
            options={people.map((p) => ({ value: p.ID, label: p.TeamName ? `${p.DisplayName} (${p.TeamName})` : p.DisplayName }))} />
        )}
      </Space>
      {data?.error && <Alert type="error" showIcon message={data.error} style={{ marginBottom: 12 }} />}
      <Typography.Paragraph>
        {data?.person ? `${data.person.DisplayName}'s shifts, today to 21 days from now. Pick one to see who could swap it.` : ''}
        {data?.min_notice_days > 0 && ` Shifts need ${data.min_notice_days} days notice: the first you can pick starts ${dayjs(data.earliest).format('ddd MM/DD')}.`}
      </Typography.Paragraph>
      <Table size="small" rowKey="Start" loading={!data} dataSource={data?.shifts || []} pagination={false}
        locale={{ emptyText: <Empty description="No shifts in the next 21 days." /> }}
        onRow={(r) => (r.TooSoon ? {} : { onClick: () => setShift(r), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Shift', key: 'shift', render: (_, r) => (r.TooSoon
            ? <Typography.Text type="secondary">{fmtShift(r)}</Typography.Text>
            : <Typography.Link>{fmtShift(r)}</Typography.Link>) },
          { title: 'Department', dataIndex: 'Department', key: 'dept' },
          { title: 'Hours', dataIndex: 'Hours', key: 'hrs', align: 'right' },
          { title: 'Forum', key: 'forum', render: (_, r) => (r.PostID ? <Tag color="blue">On the forum</Tag>
            : r.TooSoon ? <Typography.Text type="secondary">Too soon to post</Typography.Text> : '') },
        ]} />
      <ExploreDrawer data={data} shift={shift} userId={userId}
        onClose={() => setShift(null)} onPublish={() => setPublishing(shift)} />
      <PublishModal data={data} shift={publishing} userId={userId} self={self}
        onClose={() => setPublishing(null)}
        onDone={() => navigate('/shift-swap-forum')} />
    </>
  );
}

// Who could swap a shift; also the forum's Show Swap Matches (shift.PostID
// set: already on the forum, so no Publish).
export function ExploreDrawer({ data, shift, userId, onClose, onPublish }) {
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [allSkills, setAllSkills] = useState(true);
  const [hideLeave, setHideLeave] = useState(true);
  const [personFor, setPersonFor] = useState(null);
  const [prefs, setPrefs] = useState([]);
  const manager = !!data?.is_manager;

  useEffect(() => {
    if (!shift) return;
    setRes(null);
    setError(null);
    api.get('/api/shift-swap/explore', { params: { start: shift.Start, ...(userId ? { user_id: userId } : {}) } })
      .then((r) => setRes(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Could not work out who could swap.'));
  }, [shift, userId]);
  const openPerson = (id) => {
    setPersonFor(id);
    if (!prefs.length) api.get('/api/availability/preferences').then((r) => setPrefs(r.data || [])).catch(() => {});
  };

  const s = res?.row?.Shifts?.[0];
  return (
    <Drawer open={!!shift} onClose={onClose} width={1100} destroyOnClose
      title={shift ? `${data?.person?.DisplayName || ''} · ${fmtShift(shift)} · ${shift.Department}` : ''}
      extra={shift && (shift.PostID ? <Tag color="blue">On the forum</Tag>
        : <Button type="primary" onClick={onPublish}>Publish shift to swap forum</Button>)}>
      <Space wrap align="center" style={{ marginBottom: 12 }}>
        <Checkbox checked={hideLeave} disabled={!manager} onChange={(e) => setHideLeave(e.target.checked)}>Hide people on leave</Checkbox>
        <Checkbox checked={onlyAvailable} disabled={!manager} onChange={(e) => setOnlyAvailable(e.target.checked)}>Only matching availability</Checkbox>
        <Checkbox checked={allSkills} onChange={(e) => setAllSkills(e.target.checked)}>Only matching all skills</Checkbox>
      </Space>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {!res && !error ? <Spin /> : s ? (
        <CoverSwapLists a={res.row} s={s} onlyAvailable={onlyAvailable} allSkills={allSkills} hideLeave={hideLeave}
          showCover={manager} onOpen={manager ? openPerson : null} />
      ) : !error && <Empty description="No one found for this shift." />}
      {manager && (
        <AvailabilityDrawer userId={personFor} prefs={prefs} onClose={() => setPersonFor(null)} />
      )}
    </Drawer>
  );
}

// "Publish shift to swap forum": the user's own words, their current WFM
// Availability (shown with the entry), and an optional note.
// absenceId: posted from the WFM Shift Swap Board for that absence
// notification (no "not an absence notification" sentence).
export function PublishModal({ data, shift, userId, self, absenceId, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(''); }, [shift]);
  if (!shift || !data?.person) return null;
  const when = `${dayjs(shift.Start).format('ddd MM/DD/YYYY')} ${dayjs(shift.Start).format('h:mm A')} to ${dayjs(shift.End).format('h:mm A')}`;
  const save = () => {
    setSaving(true);
    api.post('/api/shift-swap/posts', {
      start: shift.Start, note, ...(userId ? { user_id: userId } : {}), ...(absenceId ? { absence_id: absenceId } : {}),
    })
      .then(() => { message.success('Published to the Shift Swap Forum.'); onDone(); })
      .catch((e) => message.error(e.response?.data?.error || 'Could not publish it.'))
      .finally(() => setSaving(false));
  };
  const whose = self ? 'your' : `${data.person.DisplayName}'s`;
  return (
    <Modal open onCancel={onClose} onOk={save} okText="Publish" confirmLoading={saving} width={720}
      title="Publish shift to swap forum">
      <Typography.Paragraph>
        Would you like to publish {whose} shift {when} to the swap forum for others to view?
        {!absenceId && ` This is not an absence notification and still requires you to fulfill your shift obligation
        if your Supervisor cannot confirm a shift swap before the deadline`}
      </Typography.Paragraph>
      <AvailabilityBlock person={data.person} availability={data.availability} shift={shift} self={self} />
      <Typography.Text strong style={{ display: 'block', marginTop: 12 }}>Note (optional)</Typography.Text>
      <Input.TextArea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}

function AvailabilityBlock({ person, availability, shift, self }) {
  const a = { Name: person.DisplayName, Availability: availability || [] };
  return (
    <div>
      <AbsentAvailability a={a} day={dayjs(shift.Start).day() || 7}
        title={self ? 'Your WFM Availability is currently set to:' : `${person.DisplayName}'s WFM Availability is currently set to:`} />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {(availability || []).length ? `This is shown to others with ${self ? 'your' : 'their'} post automatically.`
          : 'No WFM Availability is entered; others will see it as not set.'}
      </Typography.Text>
    </div>
  );
}
